import type { TranslationFunction } from "@src/packages/i18n";
import {
  getUsStateName,
  inferRegionCode,
} from "@src/packages/costs/estimates/locations";
import type { OnboardingData } from "../types";

export type ChipDescriptor = {
  key: string;
  label: string;
};

const PRIMARY_INTENT_KEYS: Record<string, string> = {
  preserve: "chips.primary_intent.preserve",
  grow: "chips.primary_intent.grow",
  donate: "chips.primary_intent.donate",
  learn: "chips.primary_intent.learn",
};

const TREATMENT_INTENT_KEYS: Record<string, string> = {
  // New goal values
  exploring: "chips.treatment_intent.exploring",
  preserve: "chips.treatment_intent.preserve",
  "sperm-preserve": "chips.treatment_intent.sperm_preserve",
  "embryo-preserve": "chips.treatment_intent.embryo_preserve",
  pregnancy: "chips.treatment_intent.pregnancy",
  "egg-donation": "chips.treatment_intent.egg_donation",
  "sperm-donation": "chips.treatment_intent.sperm_donation",
  adoption: "chips.treatment_intent.adoption",
  surrogacy: "chips.treatment_intent.surrogacy",
  // Legacy intent values
  Exploring: "chips.treatment_intent.exploring",
  Preservation: "chips.treatment_intent.preserve",
  Donation: "chips.treatment_intent.donation",
  Conception: "chips.treatment_intent.trying_naturally",
  StartingTreatment: "chips.treatment_intent.starting_treatment",
  InTreatment: "chips.treatment_intent.in_treatment",
};

const CONDITION_KEYS: Record<string, string> = {
  pcos: "chips.condition.pcos",
  endo: "chips.condition.endo",
  thyroid: "chips.condition.thyroid",
  irregular: "chips.condition.irregular",
  loss: "chips.condition.loss",
  cancer: "chips.condition.cancer",
  none: "chips.condition.none",
  unsure: "chips.condition.unsure",
};

const PARTNER_KEYS: Record<string, string> = {
  "with-partner": "chips.partner.with_partner",
  "using-donor": "chips.partner.using_donor",
  solo: "chips.partner.solo",
  unsure: "chips.partner.unsure",
};

const TIMELINE_KEYS: Record<string, string> = {
  asap: "chips.timeline.asap",
  "3-months": "chips.timeline.three_months",
  "6-months": "chips.timeline.six_months",
  exploring: "chips.timeline.exploring",
};

export function deriveChips(
  data: OnboardingData,
  t: TranslationFunction,
): ChipDescriptor[] {
  const chips: ChipDescriptor[] = [];

  // Merge primary + treatment intent into one chip: "Preservation · Embryo freezing"
  if (data.primaryIntent) {
    const primaryKey = PRIMARY_INTENT_KEYS[data.primaryIntent];
    const primary = primaryKey ? t(primaryKey) : data.primaryIntent;
    const treatmentKey = data.treatmentIntent
      ? TREATMENT_INTENT_KEYS[data.treatmentIntent]
      : null;
    const treatment = treatmentKey
      ? t(treatmentKey)
      : (data.treatmentIntent ?? null);
    const exploringLabel = t("chips.treatment_intent.exploring");
    const label =
      treatment && treatment !== exploringLabel && treatment !== primary
        ? `${primary} · ${treatment}`
        : primary;
    chips.push({ key: "intent", label });
  }

  if (data.gender) {
    chips.push({
      key: "sex",
      label: data.gender === "AFAB" ? "♀" : "♂",
    });
  }

  if (data.age != null) {
    chips.push({ key: "age", label: `🎂 ${data.age}` });
  } else if (data.dateOfBirth) {
    // Derive age from DOB
    const dob = new Date(data.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    if (age >= 18 && age <= 65) {
      chips.push({ key: "age", label: `🎂 ${age}` });
    }
  }

  if (data.ethnicity.length > 0) {
    if (data.ethnicity.length === 1) {
      chips.push({ key: "ethnicity", label: data.ethnicity[0]! });
    } else {
      chips.push({
        key: "ethnicity",
        label: t("chips.ethnicities_count", {
          params: { count: String(data.ethnicity.length) },
        }),
      });
    }
  }

  if (data.conditions.length > 0) {
    if (data.conditions.length <= 2) {
      for (const c of data.conditions) {
        const conditionKey = CONDITION_KEYS[c];
        chips.push({
          key: `cond-${c}`,
          label: conditionKey ? t(conditionKey) : c,
        });
      }
    } else {
      chips.push({
        key: "conditions",
        label: t("chips.conditions_count", {
          params: { count: String(data.conditions.length) },
        }),
      });
    }
  }

  if (data.partnerChoice) {
    const partnerKey = PARTNER_KEYS[data.partnerChoice];
    chips.push({
      key: "partner",
      label: partnerKey ? t(partnerKey) : data.partnerChoice,
    });
  }

  if (data.timeline) {
    const timelineKey = TIMELINE_KEYS[data.timeline];
    chips.push({
      key: "timeline",
      label: timelineKey ? t(timelineKey) : data.timeline,
    });
  }

  const regionCode = inferRegionCode(data.country, data.zipCode);
  if (regionCode) {
    chips.push({
      key: "location",
      label: getUsStateName(regionCode) ?? regionCode,
    });
  }

  if (data.hasEmployerBenefits != null) {
    if (data.employerBenefit) {
      chips.push({ key: "benefit", label: data.employerBenefit });
    } else if (data.hasEmployerBenefits === false) {
      chips.push({ key: "benefit", label: t("chips.no_coverage") });
    }
  }

  return chips;
}
